
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-[#FF6B6B] text-white hover:bg-[#FF5252] shadow-sm hover:shadow",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm hover:shadow",
        outline:
          "border-2 border-[#FF6B6B] bg-white hover:bg-[#FFF0F0] text-[#FF6B6B] shadow-sm hover:shadow",
        secondary:
          "bg-[#FFF0F0] text-[#FF6B6B] hover:bg-[#FFE6E6] shadow-sm hover:shadow",
        ghost: "hover:bg-[#FFF0F0] hover:text-[#FF6B6B]",
        link: "text-[#FF6B6B] underline-offset-4 hover:underline",
        gradient: "bg-gradient-to-r from-[#FF6B6B] to-[#FF8585] text-white hover:shadow-lg transition-all duration-300 shadow-md",
        coral: "bg-[#FF6B6B] text-white hover:bg-[#FF5252] shadow-sm hover:shadow transition-all duration-300",
        disabled: "bg-gray-200 text-gray-500 cursor-not-allowed shadow-none hover:bg-gray-200 hover:shadow-none",
        purpleGradient: "bg-gradient-to-r from-[#FF6B6B] to-[#FF8585] text-white shadow-md hover:shadow-lg transition-all duration-300",
        white: "bg-white text-[#FF6B6B] border border-gray-200 hover:border-[#FF6B6B] hover:shadow transition-all duration-300",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-md px-4 text-xs",
        lg: "h-12 rounded-md px-8 text-base",
        xl: "h-14 rounded-lg px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
